import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Sparkles, TrendingUp, Target, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Insights = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/documentation')} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Docs
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-purple-600" />
            <h1 className="text-4xl font-bold text-slate-900">AI Insight Reports</h1>
          </div>
          <p className="text-lg text-slate-600">Harness the power of AI to gain deep insights about your leads and opportunities.</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>What are AI Insights?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                AI Insight Reports use advanced machine learning algorithms to analyze your lead data and provide actionable recommendations. The system examines patterns, behaviors, and attributes to help you prioritize your outreach efforts.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-slate-700">
                  <strong>Pro Tip:</strong> AI insights are generated automatically when leads are enriched and updated regularly as new data becomes available.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Key Insight Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Engagement Score</h3>
                  <p className="text-sm text-slate-600">Predicts how likely a lead is to engage with your outreach based on historical patterns and industry benchmarks.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Conversion Probability</h3>
                  <p className="text-sm text-slate-600">Estimates the likelihood of converting this lead into a customer based on similar successful conversions.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Priority Ranking</h3>
                  <p className="text-sm text-slate-600">AI-generated ranking that helps you focus on leads with the highest potential ROI.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                How to Use Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-3 text-slate-700">
                <li>Navigate to any lead detail page</li>
                <li>Look for the "AI Insights" card on the right sidebar</li>
                <li>Review the key metrics and recommendations</li>
                <li>Use the "Priority" indicator to plan your outreach strategy</li>
                <li>Check back regularly as insights update with new data</li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                Best Practices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Ensure leads are fully enriched for most accurate insights</li>
                <li>Combine AI insights with your own judgment and industry knowledge</li>
                <li>Track outcome data to help improve AI predictions over time</li>
                <li>Use insights to segment leads for targeted campaigns</li>
              </ul>
            </CardContent>
          </Card>

          <div className="flex gap-3 pt-6">
            <Button onClick={() => navigate('/documentation')} className="bg-indigo-600 hover:bg-indigo-700">
              Browse All Documentation
            </Button>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights;
